import * as React from 'react';
import { color } from '@haldi/style-guide';
import * as Sentry from '@sentry/react';
import { GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useRecoilState } from 'recoil';

import {
  HEADER_IPCOUNTRY,
  HEADER_VALID_CODES,
  SITE_SURVEY_PATH,
  SITE_SURVEY_PREGNANCY_PATH,
  SITE_TITLE
} from '@src/config/constants';
import { IS_BROWSER, IS_PRODUCTION_ENV } from '@src/config/settings';
import { useGetSurveyQuery } from '@src/generated/hooks';
import { useBackgroundColor } from '@src/hooks/useBackgroundColor';
import { useFirebaseAuth } from '@src/hooks/useFirebaseAuth';
import { surveyAtom } from '@src/recoil/atoms/survey';
import { AppErrorComponent } from '@src/services/app/components/AppErrorComponent';
import { AppLoader } from '@src/services/app/components/AppLoader';
import { SurveyRenderer } from '@src/services/surveys/components/SurveyRenderer';

export interface RouteProps {
  ipCountry: string;
}

/**
 * @name Route
 * @description Simple page to render our survey
 */
export const Route = (props: RouteProps) => {
  // Hooks
  const { ipCountry } = props;
  const { query, push } = useRouter();

  // Setup
  const { id: surveyId, ...restParams } = query;
  const id = surveyId as string;

  // Redirects
  if (!HEADER_VALID_CODES.includes(ipCountry) && IS_PRODUCTION_ENV) {
    if (!IS_BROWSER) return null;

    push('/lp/international');
    return null;
  }
  if (id === 'pregnancy') {
    if (!IS_BROWSER) return null;

    // A/B testing
    // const pathname =
    //   Math.random() > 0.5 ? `${SITE_SURVEY_PATH}-3` : `${SITE_SURVEY_PATH}-4`;

    push({ pathname: SITE_SURVEY_PREGNANCY_PATH, query: restParams });
    return null;
  }

  // Hooks needed for survey
  useFirebaseAuth();
  const [atom, setAtom] = useRecoilState(surveyAtom);
  useBackgroundColor(color.turmeric);
  const { data, error, loading } = useGetSurveyQuery({
    variables: { input: { id } }
  });

  // Life Cycle
  React.useEffect(() => {
    if (data) setAtom({ ...atom, survey: data.getSurvey });
  }, [data]);

  // 🔌 Short Circuit
  if (loading) return <AppLoader state={true} />;
  if (error) {
    if (!IS_BROWSER) return null;

    // Happens when the survey id no longer exists or is incorrect
    // Make sure to pass on all query params to the correct survey
    push({ pathname: SITE_SURVEY_PATH, query: restParams });
    return null;
  }
  if (!data) {
    Sentry.captureException('Failed to load data');
    return <AppErrorComponent message="Failed to load data" />;
  }

  return (
    <>
      <Head>
        <title>{`${atom.survey.name} | ${SITE_TITLE}`}</title>
      </Head>

      <main>{atom.survey.questions?.length !== 0 && <SurveyRenderer />}</main>
    </>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { req } = context;

  const ipCountry = req?.headers[HEADER_IPCOUNTRY] ?? 'US';

  return { props: { ipCountry } };
};

export default Route;
